import { useEffect, useState } from 'react'
import { notifyFollow } from '../lib/followNotify'
import { useNavigate } from 'react-router-dom'
import { usePlans } from './TonightMode'
import { Users, Check } from 'lucide-react'
import { Sheet } from './UI'
import { Avatar } from './Avatar'
import { supabase } from '../lib/supabase'
import { fetchCompanions, fetchMatches } from '../lib/company'
import { companionsTitle, matchReason } from '../lib/companyLine'
import { publicName } from '../lib/displayName'
import { invalidateFollows } from '../hooks/useFollows'
import { logEvent, logFollow } from '../lib/analytics'

// ── CompanionsSheet ────────────────────────────────────────────────────────────
// The door behind the company line: the few visible people within a season of you
// on this show (show_companions: public profiles only, blocks both ways honoured,
// season only, and it answers only when YOUR profile is public, so seeing is
// symmetric). Two actions, in order: Follow, then Watch together (the existing
// watch-together function, which requires the follow server-side).
// People is a per-feature unlock (basic-by-default): when it's off, the sheet says
// what's behind the door and offers the unlock in context instead of a dead end.
export function CompanionsSheet({ show, user, open, onClose, featureFlags, progress }) {
  const navigate = useNavigate()
  const peopleOn = !featureFlags || featureFlags.isEnabled('people')
  const [rows, setRows] = useState(null)
  const [matches, setMatches] = useState([])   // near matches: library overlap, not on this show necessarily
  const [busy, setBusy] = useState(null)
  const [done, setDone] = useState({})   // id → 'followed' | 'invited'
  const [me, setMe] = useState(null)      // { is_public }

  useEffect(() => {
    if (!open || !show?.id || !user?.id) return
    let alive = true
    setRows(null)
    logEvent('company_sheet_open', { show: show.id, people_on: peopleOn })
    if (!peopleOn || user?.is_anonymous) return
    Promise.all([
      fetchCompanions(show.id, 8),
      supabase.from('profiles').select('is_public').eq('id', user.id).maybeSingle().then(r => r.data, () => null),
      fetchMatches(5),
    ]).then(([list, prof, near]) => {
      if (!alive) return
      setRows(list); setMe(prof)
      const onShow = new Set((list || []).map(r => r.id))
      setMatches((near || []).filter(m => !onShow.has(m.id)))
    })
    return () => { alive = false }
  }, [open, show?.id, user?.id, user?.is_anonymous, peopleOn])

  const follow = async (p) => {
    setBusy(p.id)
    const { error } = await supabase.from('follows').insert({ follower_id: user.id, following_id: p.id })
    setBusy(null)
    if (error && error.code !== '23505') return
    invalidateFollows(user.id); logFollow('companions'); notifyFollow(p.id)
    setDone(d => ({ ...d, [p.id]: 'followed' }))
    setRows(rs => (rs || []).map(r => r.id === p.id ? { ...r, following: true } : r))
  }

  // Phase 2 (2026-10-18): the one action a named companion implies. Puts the episode on
  // MY Tonight list (the existing plan rail) and sends the same watch-together invite,
  // so the other person gets the ask and I get the moon. Needs my position to be known.
  const plans = usePlans()
  const tonight = async (p) => {
    try {
      const s = progress?.season || 1, e = progress?.episode || 1
      if (plans?.toggle) await plans.toggle(show, s, e, null)
    } catch { /* the invite still goes */ }
    logEvent('company_tonight', { show: show.id })
    await invite(p)
  }
  const invite = async (p) => {
    setBusy(p.id)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/watch-together', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ action: 'send', recipient_id: p.id, show_id: show.id, show_name: show.name }),
      })
      if (res.ok) { setDone(d => ({ ...d, [p.id]: 'invited' })); logEvent('company_invite', { show: show.id }) }
    } catch { /* the button simply stays */ }
    setBusy(null)
  }

  const makePublic = async () => {
    setBusy('me')
    const { error } = await supabase.from('profiles').update({ is_public: true }).eq('id', user.id)
    setBusy(null)
    if (!error) { setMe({ is_public: true }); fetchCompanions(show.id, 8).then(setRows) }
  }

  const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 6 }
  const H = { fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4 }
  const P = { fontSize: 13.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }
  const pill = (primary) => ({ height: 36, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: primary ? 'none' : '1px solid var(--border)', background: primary ? 'var(--amber)' : 'var(--bg-card)', color: primary ? 'var(--amber-ink)' : 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 })

  return (
    <Sheet open={open} onClose={onClose} label="Who else is watching" from="bottom" panelStyle={{ padding: '10px 20px calc(24px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
      <div style={EYE}><Users size={12} />{show?.name}</div>
      {user?.is_anonymous ? (
        <>
          <div style={H}>Save your list to be seen.</div>
          <div style={P}>Guest libraries are never shown to anyone, and seeing people here is symmetric. Save your list with an email and your profile can appear to the people on your shows.</div>
          <button onClick={() => { onClose?.(); navigate('/login?ref=company') }} className="ui-hit cta-glow" style={{ ...pill(true), marginTop: 18, width: '100%', height: 46, fontSize: 15 }}>Save your list</button>
          <button onClick={onClose} style={{ marginTop: 8, width: '100%', height: 42, borderRadius: 'var(--radius-pill)', border: 'none', background: 'none', color: 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 14, cursor: 'pointer' }}>Not now</button>
        </>
      ) : !peopleOn ? (
        <>
          <div style={H}>There are people on this show.</div>
          <div style={P}>NextEp starts as just the tracker. Turn on People to see who’s within a season of you, follow them, and watch together. Only public profiles appear, and only if yours is public too.</div>
          <button onClick={() => { featureFlags?.unlockFeature?.('people'); logEvent('feature_unlock_in_context', { key: 'people', src: 'company' }) }} className="ui-hit cta-glow" style={{ ...pill(true), marginTop: 18, width: '100%', height: 46, fontSize: 15 }}>Turn on People</button>
          <button onClick={onClose} style={{ marginTop: 8, width: '100%', height: 42, borderRadius: 'var(--radius-pill)', border: 'none', background: 'none', color: 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 14, cursor: 'pointer' }}>Not now</button>
        </>
      ) : rows == null ? (
        <div style={{ ...P, padding: '18px 0 8px' }}>Looking…</div>
      ) : me && me.is_public === false ? (
        <>
          <div style={H}>Your profile is private.</div>
          <div style={P}>Seeing people here is symmetric: they appear to you only if you appear to them. Make your profile public to open the door. Your episode position is never shown, only the season you’re on.</div>
          <button onClick={makePublic} disabled={busy === 'me'} className="ui-hit cta-glow" style={{ ...pill(true), marginTop: 18, width: '100%', height: 46, fontSize: 15 }}>{busy === 'me' ? 'Opening…' : 'Make my profile public'}</button>
          <button onClick={onClose} style={{ marginTop: 8, width: '100%', height: 42, borderRadius: 'var(--radius-pill)', border: 'none', background: 'none', color: 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 14, cursor: 'pointer' }}>Keep it private</button>
        </>
      ) : (
        <>
          <div style={H}>{companionsTitle(rows.length)}</div>
          <div style={P}>{rows.length ? 'Within a season of where you are. Follow to see their marks, or ask to watch together.' : matches.length ? 'Nobody near your season on this one yet, but a few libraries overlap yours. Public profiles only.' : 'People who join this show near your season will show up here. Public profiles only.'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
            {[...rows, ...matches.map(m => ({ ...m, _match: true }))].map((p, i) => {
              const state = done[p.id] || (p.following ? 'followed' : null)
              const firstMatch = p._match && (i === 0 || !rows.length || i === rows.length)
              return (
                <div key={p.id} style={{ display: 'contents' }}>
                {firstMatch && (
                  <div style={{ ...EYE, marginTop: rows.length ? 10 : 0 }} aria-label="Near matches">Near matches</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 'var(--radius)', background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <div onClick={() => { onClose?.(); navigate(`/u/${p.username}`) }} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: 'pointer' }}>
                    <Avatar name={publicName(p)} avatarUrl={p.avatar_url} size={34} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: 'var(--text-strong)', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{publicName(p)}</div>
                      <div style={{ color: 'var(--text-dim)', fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p._match ? matchReason(p) : p.season ? `On season ${p.season}` : 'Just started'}</div>
                    </div>
                  </div>
                  {state === 'invited' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-dim)', fontSize: 12.5 }}><Check size={13} /> Asked for tonight</span>
                  ) : state === 'followed' ? (
                    <button onClick={() => tonight(p)} disabled={busy === p.id} className="ui-hit" style={pill(true)} aria-label={`Ask ${publicName(p)} to watch tonight`}>{busy === p.id ? '…' : 'Watch tonight?'}</button>
                  ) : (
                    <button onClick={() => follow(p)} disabled={busy === p.id} className="ui-hit" style={pill(false)}>{busy === p.id ? '…' : 'Follow'}</button>
                  )}
                </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </Sheet>
  )
}
